import {
  CheckCircleOutlined,
  DownOutlined,
  LockOutlined,
  UpOutlined,
} from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Card, Tooltip, Typography } from 'antd';
import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import StepStateContext from './StepStateContext';

const { Title } = Typography;

interface StepCardProps {
  completed?: boolean;
  title?: string;
  stepNumber?: number;
  children?: React.ReactNode;
  actions?: {
    variant?: 'text' | 'outlined' | 'dashed' | 'link' | 'solid' | 'filled';
    name: string;
    onClick?: () => void;
    disabled?: boolean;
    loading?: boolean;
  }[];
  headerRight?: React.ReactNode;
}

const StyledCard = styled(Card)`
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .ant-card-body {
    padding: 24px;
  }
`;

const StepIndicator = styled.div`
  width: 32px;
  height: 32px;
  background-color: ${({ theme }) => theme.palette.gray[100]};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.palette.secondary.main};
  font-weight: 600;
  font-size: 16px;
  margin-right: 12px;
  border: 1px solid ${({ theme }) => theme.palette.blue[100]};
`;

const CompletedIndicator = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  background-color: ${({ theme }) => theme.palette.green[9]};
  color: ${({ theme }) => theme.palette.green[500]};
  border: 1px solid ${({ theme }) => theme.palette.green[8]};
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
`;

const HeaderTitle = styled(Title)`
  margin: 0 !important;
  font-size: 18px !important;
  color: ${({ theme }) => theme.palette.text.primary} !important;
`;

const CollapseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: ${({ theme }) => theme.palette.gray[600]};

  &:hover {
    color: ${({ theme }) => theme.palette.secondary.main};
  }
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => `repeating-linear-gradient(
    to right,
    ${theme.palette.gray[300]} 0,
    ${theme.palette.gray[300]} 2px,
    transparent 2px,
    transparent 4px
  )`};
  margin: 16px -24px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StepCard: React.FC<StepCardProps> = ({
  completed = false,
  title = '',
  stepNumber = 0,
  children,
  actions = [],
  headerRight,
}) => {
  const { messages } = useTypedIntl();
  // Sequential flows (employee onboarding) gate steps that are not reachable
  // yet. A gated step can still be opened and read — only its actions are
  // disabled, so the user can look ahead but not save out of order.
  const { locked, lockedHint, submitting, autoExpand } =
    useContext(StepStateContext);

  // The active step opens on mount. The list is remounted after every save, so
  // completing a step expands whichever one becomes active next.
  const [isCollapsed, setIsCollapsed] = useState(!autoExpand);

  const handleCollapse = () => setIsCollapsed(!isCollapsed);

  return (
    <StyledCard>
      <HeaderContainer onClick={handleCollapse}>
        <HeaderLeft>
          {completed ? (
            <CompletedIndicator>
              <CheckCircleOutlined />
            </CompletedIndicator>
          ) : (
            <StepIndicator>{stepNumber}</StepIndicator>
          )}
          <HeaderTitle level={4}>{title}</HeaderTitle>
        </HeaderLeft>
        <div>
          {/* Hidden while gated: it would otherwise let the step be ticked off
              out of order. */}
          {!locked && headerRight}
          {locked && (
            <Tooltip title={lockedHint}>
              <CollapseButton aria-label={lockedHint}>
                <LockOutlined />
              </CollapseButton>
            </Tooltip>
          )}
          <CollapseButton
            aria-label={
              isCollapsed
                ? messages['common.expand']
                : messages['common.collapse']
            }
          >
            {isCollapsed ? <DownOutlined /> : <UpOutlined />}
          </CollapseButton>
        </div>
      </HeaderContainer>

      {!isCollapsed && (
        <>
          <Divider />
          {children}
          <ButtonContainer>
            {actions.map((act) => {
              // `submitting` comes from the parent flow (the step's save is in
              // flight); `act.loading` is the step's own work, e.g. an S3
              // upload that runs before the save.
              const isLoading = !!act.loading || !!submitting;
              const button = (
                <AppButton
                  key={act.name}
                  variant={act.variant}
                  name={act.name}
                  onClick={() => act.onClick?.()}
                  disabled={act.disabled || isLoading || locked}
                  loading={isLoading}
                />
              );
              // A disabled button gives no reason on its own, so explain the
              // gate on hover.
              return locked ? (
                <Tooltip key={act.name} title={lockedHint}>
                  <span>{button}</span>
                </Tooltip>
              ) : (
                button
              );
            })}
          </ButtonContainer>
        </>
      )}
    </StyledCard>
  );
};

export default StepCard;
